import {
    ActionIcon,
    Alert,
    Badge,
    Button,
    Card,
    Center,
    Grid,
    Group,
    Image,
    Paper,
    SegmentedControl,
    Stack,
    Text,
    useMantineTheme,
} from "@mantine/core"
import {
    AttomAvm,
    EHomeSellingOptionActions,
    EUserEventType,
    HomeEquityCalculator,
    homeHooks,
    HomeRefinanceOptions,
    HomeSellingOptions,
    HomeSellScore,
    HomeSendMessageModal,
    HomeUtils,
    IBundle,
    IHomeSellingOptionSubmitValues,
    IRefinanceFormValues,
    ZillowAvm,
} from "features/homes"
import {Content, LoaderDefault, SegmentedDeviation} from "components/common"
import {useNavigate, useParams} from "react-router-dom"
import {ETaskFormTypes, taskHooks} from "features/tasks"
import {IconBath, IconBed, IconSquare} from "@tabler/icons"
import StockHome from "assets/stock-home.jpg"
import {useUserRoutes} from "routes"
import {ConciergeDetails} from "features/concierges"
import {offerHooks} from "features/offers"
import {taskStepHooks, TaskStepsCurrentStep} from "features/task-steps"
import {useMemo, useState} from "react"
import {AlertCircle, CircleCheck, Confetti, RotateThreeSixty} from "tabler-icons-react"
import {GoogleMap, StreetViewPanorama} from "@react-google-maps/api"
import {NumberUtils} from "utils";
import {Carousel, Embla} from "@mantine/carousel"

export function HomeDetails() {
    const {homeId} = useParams()
    const navigate = useNavigate()
    const [carouselValue, setCarouselValue] = useState('listed');
    const {routesResolver} = useUserRoutes()
    const theme = useMantineTheme()
    const [embla, setEmbla] = useState<Embla | null>(null)
    const {data: home, isLoading: isLoadingHome} = homeHooks.useGetById(
        parseInt(homeId || ""),
        {
            populate: "*",
        }
    )

    const {mutateAsync: requestCMA, isLoading: isRequestingCMA} =
        homeHooks.useRequestCMA()

    const {mutateAsync: userClickEvent, isLoading: isUserClickEventLoading} =
        homeHooks.useUserClickEvent()

    const {data: recents, isLoading: isLoadingRecents} =
        homeHooks.useGetRecents(parseInt(homeId || ""))
    const {mutateAsync: sendMessage} = homeHooks.useSendMessage()
    // const { isLoading: isLoadingRecentlySold, data: recentlySold } =
    //   homeHooks.useGetRecentlySold(parseInt(homeId || ""))
    const {isLoading: isLoadingSellScore, data: sellScore} =
        homeHooks.useGetSellScore(parseInt(homeId || ""))
    const {
        isLoading: isLoadingEstimatedMarketValue,
        data: estimatedMarketValue,
    } = homeHooks.useGetEstimatedMarketValue(parseInt(homeId || ""))

    const {data: tasks, isLoading: loadingTasks} = taskHooks.useGet(
        {
            filters: {home: parseInt(homeId || "")},
        },
        home?.data?.attributes?.sellStatus ? true : false
    )

    const {data: offers} = offerHooks.useGet(
        {
            filters: {homeId: parseInt(homeId || "")},
        },
        home?.data?.attributes?.sellStatus ? true : false
    )

    const {data: taskSteps} = taskStepHooks.useGet(
        {
            filters: {
                category: "sell",
                subCategory: home?.data?.attributes?.sellStatus,
            },
        },
        home?.data?.attributes?.sellStatus ? true : false
    )

    const sortedTaskSteps = useMemo(
        () =>
            taskSteps?.data.sort(
                (a, b) => (a.attributes.sequence || 0) - (b.attributes.sequence || 0)
            ),
        [taskSteps]
    )

    const currentStep = useMemo(
        () => getCurrentStep(),
        [taskSteps, tasks, loadingTasks]
    )

    const currentTask = useMemo(
        () => getCurrentTask(),
        [taskSteps, tasks, loadingTasks]
    )

    if (!home) return null

    function getCurrentStep() {
        if (!tasks || !sortedTaskSteps) return null

        // Get the index of the earliest step (step with lowest sequence) where any of its corresponding tasks are not completed
        let currentStep = sortedTaskSteps[0]
        for (let i = 0; i < sortedTaskSteps.length; i++) {
            const taskStep = sortedTaskSteps[i]
            const incomplete = tasks?.data.find(
                (t) =>
                    t.attributes.stepId === taskStep.attributes.stepId &&
                    !t.attributes.completedOn
            )

            if (incomplete) {
                currentStep = taskStep
                break
            }
        }

        return currentStep
    }

    function getFloorCeilingPrice() {
        if (estimatedMarketValue?.price) {
            const deviation = estimatedMarketValue.price * 0.25
            return {
                floor: estimatedMarketValue.price - deviation,
                ceiling: estimatedMarketValue.price + deviation,
            }
        } else {
            return {
                floor: 0,
                ceiling: 0,
            }
        }
    }

    function getCurrentTask() {
        if (!tasks || !currentStep) return null

        return tasks?.data
            ?.sort(
                (a, b) => (a.attributes.sequence ?? 0) - (b.attributes.sequence ?? 0)
            )
            .find(
                (task) =>
                    task.attributes.stepId === currentStep?.attributes.stepId &&
                    !task.attributes.completedOn
            )
    }


    async function onSellingOptionModalClick(values: IHomeSellingOptionSubmitValues) {
        switch (values.action) {
            case EHomeSellingOptionActions.SimpleSale:
                navigate(
                    routesResolver.getInteractiveFormByHomeId(
                        parseInt(homeId || ""),
                        ETaskFormTypes.SIMPLE_SALE
                    )
                )
                break
            case EHomeSellingOptionActions.Consultation:
                await userClickEvent({ eventType: EUserEventType.RequestListingConsultation, homeId: home?.data?.id })
                return null
            case EHomeSellingOptionActions.CashOffer:
                // TODO: Fill with API call for Cash Offer
                return null
        }
    }

    async function onSubmitRefinanceOptions(values:IRefinanceFormValues){
        await userClickEvent({homeId: parseInt(homeId!), eventType: EUserEventType.RequestRefiConsultation, data:HomeUtils.transformRefinanceForm(values) })
    }

    function renderCarousel() {
        let show = recents?.recentlyListed.bundle
        if (carouselValue === "sold") {
            show = recents?.recentlySold.bundle
        }
        return show?.map((bundle: IBundle, i) => {
            let color = "red"
            switch (bundle.StandardStatus.toLowerCase()) {
                case "active":
                    color = "green"
                    break
                case "pending":
                    color = "yellow"
                    break
            }
            return (
                <Carousel.Slide key={i}>
                    <Card withBorder>
                        <Card.Section>
                            <Image
                                src={bundle.Media[0]?.MediaURL}
                                height={200}
                                alt="Norway"
                            />
                        </Card.Section>
                        <Group position="apart" mt="md" mb="xs">
                            <Text weight={600} size="lg">{NumberUtils.formatAsCurrency(bundle.ListPrice)}</Text>
                            <Badge radius={5} color={color} variant="light">
                                {bundle.StandardStatus}
                            </Badge>
                        </Group>
                        <Stack spacing={9}>
                            <Stack spacing={0}>
                                <Text size="xs" color="dimmed">
                                    {bundle.StreetNumber} {bundle.StreetName} {bundle.StreetDirPrefix} {bundle.StreetSuffix} {bundle.StreetDirSuffix}
                                </Text>
                                <Text size="xs" color="dimmed">
                                    {bundle.City}, {bundle.StateOrProvince} {bundle.PostalCode}
                                </Text>
                            </Stack>
                            <Group position="apart">
                                <Group spacing={6}>
                                    <Group spacing={0}>
                                        <ActionIcon variant="transparent" disabled>
                                            <IconBath size={16}/>
                                        </ActionIcon>
                                        <Text size="xs"
                                              color="dimmed">{bundle.BedroomsTotal} bed{bundle.BedroomsTotal === 1 ? "" : "s"}</Text>
                                    </Group>
                                    <Group spacing={0}>
                                        <ActionIcon variant="transparent" disabled>
                                            <IconBed size={16}/>
                                        </ActionIcon>
                                        <Text size="xs"
                                              color="dimmed">{bundle.BathroomsFull} bed{bundle.BathroomsFull === 1 ? "" : "s"}</Text>
                                    </Group>
                                    <Group spacing={0}>
                                        <ActionIcon variant="transparent" disabled>
                                            <IconSquare size={16}/>
                                        </ActionIcon>
                                        <Text size="xs"
                                              color="dimmed">{bundle.AboveGradeFinishedArea || "—"} sqft</Text>
                                    </Group>
                                </Group>
                                <Text size="xs" color="dimmed">Built {bundle.YearBuilt}</Text>
                            </Group>
                        </Stack>
                    </Card>
                </Carousel.Slide>
            )
        })
    }

    function renderBody() {
        if (HomeUtils.isBeingSold(home?.data)) {
            return (
                <Stack mb={"xl"}>
                    <Alert
                        icon={<Confetti/>}
                        variant="light"
                        color="blue"
                        title="Congratulations! You are on the way to achieving your goals."
                    >
                        <Text>
                            A special thank you for trusting us with your real estate needs.
                            Please complete the tasks below to progress through the sales
                            process.
                        </Text>
                    </Alert>
                    <Grid grow>
                        <Grid.Col sm={12} md={6}>
                            <Stack
                                h="100%"
                                sx={{
                                    position: "relative",
                                    overflow: "hidden",
                                    borderRadius: 5,
                                }}
                            >
                                {home?.data.attributes.latitude &&
                                home.data.attributes.longitude ? (
                                    <GoogleMap
                                        streetView={undefined}
                                        mapContainerStyle={{
                                            height: "100%",
                                            width: "100%",
                                        }}
                                        center={{
                                            lat: parseFloat(home.data.attributes.latitude),
                                            lng: parseFloat(home.data.attributes.longitude),
                                        }}
                                        zoom={10}
                                    >
                                        <StreetViewPanorama
                                            options={{
                                                disableDefaultUI: true,
                                                position: {
                                                    lat: parseFloat(home.data.attributes.latitude),
                                                    lng: parseFloat(home.data.attributes.longitude),
                                                },
                                                visible: true,
                                            }}
                                        />
                                    </GoogleMap>
                                ) : (
                                    <img
                                        height="100%"
                                        style={{
                                            position: "absolute",
                                            top: "50%",
                                            left: "50%",
                                            transform: "translate(-50%, -50%)",
                                        }}
                                        src={StockHome}
                                        alt={home?.data.attributes.streetAddress}
                                    />
                                )}
                            </Stack>
                        </Grid.Col>
                        <Grid.Col span={6}>
                            <Grid>
                                <Grid.Col span={12}>
                                    <Paper withBorder p="sm">
                                        <Stack>
                                            <Center>
                                                <Text fw={700} size={"xl"}>
                                                    Offers
                                                </Text>
                                            </Center>
                                            <Center>
                                                <Text fw={700} style={{fontSize: 48}}>
                                                    {offers?.data.length || "Pending"}
                                                </Text>
                                            </Center>
                                            <Center>
                                                <Button
                                                    onClick={() =>
                                                        navigate(
                                                            routesResolver.getInteractiveFormByHomeId(
                                                                parseInt(homeId || ""),
                                                                ETaskFormTypes.REVIEW_OFFERS,
                                                                currentTask?.attributes.formId ===
                                                                ETaskFormTypes.REVIEW_OFFERS
                                                                    ? currentTask?.id
                                                                    : undefined
                                                            )
                                                        )
                                                    }
                                                    disabled={offers?.data.length === 0}
                                                >
                                                    View Offers
                                                </Button>
                                            </Center>
                                        </Stack>
                                    </Paper>
                                </Grid.Col>

                                <Grid.Col span={12}>
                                    <Card withBorder p={16}>
                                        <Stack>
                                            <Stack align="center">
                                                <Text weight={600} size="lg">
                                                    Estimated Home Value
                                                </Text>
                                            </Stack>
                                            {!HomeUtils.isBeingSold(home?.data) && (
                                                <Stack align="center" mb={24}>
                                                    <Text size="sm">
                                                        Get our expert's opinion on your home's value today.
                                                    </Text>
                                                    <Button leftIcon={<RotateThreeSixty/>}>
                                                        Request Valuation Estimate
                                                    </Button>
                                                </Stack>
                                            )}
                                            <Grid w="100%">
                                                <Grid.Col md={12} lg={6}>
                                                    <AttomAvm
                                                        loading={isLoadingHome}
                                                        attomAvm={home?.data.attributes.attomAvm}
                                                    />
                                                </Grid.Col>
                                                <Grid.Col md={12} lg={6}>
                                                    <ZillowAvm
                                                        loading={isLoadingHome}
                                                        zillowAvm={home?.data.attributes.zillowAvm}
                                                    />
                                                </Grid.Col>
                                            </Grid>
                                            {!HomeUtils.isBeingSold(home?.data) && (
                                                <Alert color="yellow">
                                                    <Text span>
                                                        The values above are generated by automated
                                                        valuation models, which come with a margin of error.
                                                        Click the Request Valuation Estimate button to get a
                                                        more precise, free home valuation from your local
                                                        expert today!
                                                    </Text>
                                                </Alert>
                                            )}
                                        </Stack>
                                    </Card>
                                </Grid.Col>
                            </Grid>
                        </Grid.Col>
                    </Grid>
                    <Stack mt={24}>
                        <TaskStepsCurrentStep
                            home={home?.data}
                            tasks={tasks?.data}
                            loading={loadingTasks}
                            currentStep={currentStep}
                            currentTask={currentTask}
                        />
                    </Stack>
                </Stack>
            )
        } else {
            return (
                <>
                    <Grid>
                        <Grid.Col span={4}>
                            <Card withBorder p={16} h="100%">
                                <HomeSellScore loading={isLoadingSellScore} sellScore={sellScore} home={home?.data} />
                            </Card>
                        </Grid.Col>
                        <Grid.Col span={8}>
                            <Card withBorder p={16}>
                                <Stack>
                                    <Grid>
                                        <Grid.Col span={6}>
                                            <Text size="sm">
                                                What is my home worth?
                                            </Text>
                                            <Text weight={600} size={30}>
                                                {NumberUtils.formatAsCurrency(estimatedMarketValue?.price)}
                                            </Text>
                                        </Grid.Col>
                                        <Grid.Col span={6}>
                                            <Stack align="flex-end">
                                                <Stack maw={250} w="100%" spacing={6}>
                                                    <Text size="xs" mb={6}>
                                                        Estimated sale price range
                                                    </Text>
                                                    <SegmentedDeviation startingValue={estimatedMarketValue?.price || 0}
                                                                        formatter={NumberUtils.formatAsCurrency}
                                                                        deviationPercentage={0.25} ticks={20}/>
                                                </Stack>
                                            </Stack>
                                        </Grid.Col>
                                    </Grid>
                                    {!HomeUtils.isBeingSold(home?.data) && (
                                        <Alert icon={<AlertCircle/>} variant="light" color="blue">
                                            <Text>
                                                Automated valuations aren't as accurate as professional opinions.
                                                Contact your agent for a complete property value report.
                                            </Text>
                                        </Alert>
                                    )}
                                    {!HomeUtils.isBeingSold(home?.data) && (
                                        <Stack align="flex-start">
                                            {home?.data.attributes.cmaUrl ? (
                                                <Button 
                                                    component="a"
                                                    href={home?.data.attributes.cmaUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    onClick={() => userClickEvent({ eventType: EUserEventType.CmaRequested, homeId: home?.data?.id })}>
                                                    View Your Professional Valuation
                                                </Button>
                                            ) : (
                                                <Button
                                                    variant="outline"
                                                    disabled={home?.data.attributes.valuationRequested}
                                                    onClick={() => requestCMA(home?.data.id)}
                                                    leftIcon={
                                                        home?.data.attributes.valuationRequested ? (
                                                            <CircleCheck size={18}/>
                                                        ) : (
                                                            <RotateThreeSixty size={18}/>
                                                        )
                                                    }
                                                    loading={isRequestingCMA}
                                                >
                                                    {home?.data.attributes.valuationRequested
                                                        ? "CMA Requested"
                                                        : "Get a Professional Evaluation"}
                                            </Button>
                                        )}
                                        </Stack>
                                    )}
                                    {/*<Grid w="100%">*/}
                                    {/*    <Grid.Col md={12} lg={6}>*/}
                                    {/*        <AttomAvm*/}
                                    {/*            loading={isLoadingHome}*/}
                                    {/*            attomAvm={home?.data.attributes.attomAvm}*/}
                                    {/*        />*/}
                                    {/*    </Grid.Col>*/}
                                    {/*    <Grid.Col md={12} lg={6}>*/}
                                    {/*        <ZillowAvm*/}
                                    {/*            loading={isLoadingHome}*/}
                                    {/*            zillowAvm={home?.data.attributes.zillowAvm}*/}
                                    {/*        />*/}
                                    {/*    </Grid.Col>*/}
                                    {/*</Grid>*/}
                                </Stack>
                            </Card>
                        </Grid.Col>
                    </Grid>
                    <Content.Section mt={24}>
                        <Content.Header>
                            <Content.Title>How Much Equity Do I Have?</Content.Title>
                        </Content.Header>
                        <Content.Body p={16}>
                            <HomeEquityCalculator loading={isLoadingEstimatedMarketValue}
                                                  currentHomeMortgageValue={home?.data.attributes.currentMortgageBalance}
                                                  value={estimatedMarketValue?.price || 0}/>
                            <Alert>
                                <Stack align="center">
                                    <Text weight={600}>Ready to turn your equity into cash?</Text>
                                    <Group>
                                        <HomeSellingOptions.Button onSubmitForm={onSellingOptionModalClick}/>
                                        <HomeRefinanceOptions.Button onSubmitForm={onSubmitRefinanceOptions}/>
                                    </Group>
                                </Stack>
                            </Alert>
                        </Content.Body>
                    </Content.Section>
                    <Content.Section mt={24}>
                        <Content.Header>
                            <Content.Title>What's Happening in My Area?</Content.Title>
                        </Content.Header>
                        <Content.Body p={12}>
                            {isLoadingRecents ? <LoaderDefault text="Loading Recents"/> :
                                <Stack>
                                    <SegmentedControl
                                        value={carouselValue}
                                        onChange={(value) => {
                                            setCarouselValue(value)
                                            embla?.scrollTo(0)
                                        }}
                                        data={[
                                            {
                                                label: `Recently Listed (${recents?.recentlyListed.bundle.length})`,
                                                value: 'listed'
                                            },
                                            {
                                                label: `Recently Sold (${recents?.recentlySold.bundle.length})`,
                                                value: 'sold'
                                            },
                                        ]}
                                    />
                                    <Carousel initialSlide={0} slideSize="50%" slideGap="md" withIndicators
                                              getEmblaApi={setEmbla}
                                              slidesToScroll={2} styles={{
                                        indicators: {
                                            position: "relative",
                                            bottom: 0,
                                            marginTop: 12
                                        },
                                        indicator: {
                                            backgroundColor: theme.colors.gray[4],
                                            width: 12,
                                            height: 4,
                                            transition: 'width 250ms ease',

                                            '&[data-active]': {
                                                width: 40,
                                            },
                                        },
                                    }}>
                                        {renderCarousel()}
                                    </Carousel>
                                </Stack>}
                            {/*<RecentlySoldListTable*/}
                            {/*    disableFields={["daysOnMarket", "score"]}*/}
                            {/*    data={home?.data.attributes.recentSolds*/}
                            {/*        ?.filter((home) => home.soldAmount > 0)*/}
                            {/*        .sort(*/}
                            {/*            (a, b) =>*/}
                            {/*                new Date(b.soldDate).getTime() -*/}
                            {/*                new Date(a.soldDate).getTime()*/}
                            {/*        )*/}
                            {/*        .slice(0, 3)}*/}
                            {/*    // loading={isLoadingRecentlySold}*/}
                            {/*/>*/}
                            {/*<Group position={"right"} p={"lg"}>*/}
                            {/*    <Button*/}
                            {/*        rightIcon={<IconArrowRight/>}*/}
                            {/*        onClick={() => {*/}
                            {/*            navigate(*/}
                            {/*                routesResolver.getHomeRecentlySoldNearYouByHomeId(*/}
                            {/*                    parseInt(homeId || "")*/}
                            {/*                )*/}
                            {/*            )*/}
                            {/*        }}*/}
                            {/*    >*/}
                            {/*        See More*/}
                            {/*    </Button>*/}
                            {/*</Group>*/}
                        </Content.Body>
                    </Content.Section>
                </>
            )
        }
    }

    return (
        <Content.Body>
            <Grid gutter="xl">
                <Grid.Col md={10} sm={12}>
                    {renderBody()}
                </Grid.Col>
                <Grid.Col md={2} sm={12}>
                    <Stack sx={{position: "sticky", top: 12}}>
                        <Content.Section sx={{border: "none", background: "transparent"}}>
                            <Content.Header px={0} py={0}>
                                <Content.Title p={0}>Your Concierge</Content.Title>
                            </Content.Header>
                            <Content.Body mt={12}>
                                <ConciergeDetails
                                    concierge={home?.data?.attributes?.concierge?.data}
                                />
                                {home?.data?.attributes?.concierge?.data && (
                                    <HomeSendMessageModal.Button
                                        onSubmitForm={(values: any) =>
                                            sendMessage({
                                                ...values,
                                                homeId: home?.data.id,
                                            })
                                        }
                                    />
                                )}
                            </Content.Body>
                        </Content.Section>
                    </Stack>

                    {/*{HomeUtils.isBeingSold(home?.data) && (*/}
                    {/*  <Content.Section*/}
                    {/*    sx={{*/}
                    {/*      border: "none",*/}
                    {/*      background: "transparent",*/}
                    {/*      marginTop: "32px",*/}
                    {/*    }}*/}
                    {/*  >*/}
                    {/*    <Content.Header px={0} py={0}>*/}
                    {/*      <Content.Title p={0}>Selling Timeline</Content.Title>*/}
                    {/*    </Content.Header>*/}
                    {/*    <Content.Body mt={12}>*/}
                    {/*      <ClosingStepsTimeline*/}
                    {/*        home={home?.data}*/}
                    {/*        tasks={tasks?.data}*/}
                    {/*        taskSteps={taskSteps?.data}*/}
                    {/*      />*/}
                    {/*    </Content.Body>*/}
                    {/*  </Content.Section>*/}
                    {/*)}*/}
                </Grid.Col>
            </Grid>
        </Content.Body>
    )
}
